<template>
  <v-container fluid class="company-credit-page">
    <section class="page-hero">
      <div>
        <h1 class="page-title">เครดิตบริษัท</h1>
        <p class="page-subtitle">
          เติม ถอน และตรวจสอบยอดเครดิตพร้อมประวัติรายการของบริษัท
        </p>
      </div>
      <div class="hero-icon"><v-icon>account_balance_wallet</v-icon></div>
    </section>

    <div class="page-actions">
      <v-btn
        class="b2b-contract-btn"
        color="info"
        to="/weserve_admin_login/-/messenger/contracts"
        >สัญญา Messenger B2B<v-icon right>chevron_right</v-icon>
      </v-btn>
    </div>

    <v-card class="credit-shell glass-card">
      <v-card-text>
        <v-autocomplete
          v-model="selectedCompany"
          :items="companies"
          :search-input.sync="search"
          :loading="searching"
          item-text="display_text"
          return-object
          no-filter
          clearable
          label="เลือกบริษัท"
          no-data-text="ไม่พบบริษัท"
          @focus="ensureCompanies"
          @change="loadCredit"
        ></v-autocomplete>

        <v-layout v-if="account" row wrap>
          <v-flex xs12
            ><v-card class="balance-card pa-4 ma-1"
              ><div class="caption">เครดิตบริษัท</div>
              <div class="headline">
                {{ money(account.current_balance) }}
              </div></v-card
            ></v-flex
          >
        </v-layout>

        <v-card
          v-if="account && canMutate"
          flat
          class="credit-actions mt-4 pa-4"
        >
          <v-layout row wrap align-center>
            <v-flex xs12 sm3 class="pa-1"
              ><v-select
                v-model="action"
                :items="availableActions"
                label="รายการ"
              ></v-select
            ></v-flex>
            <v-flex xs12 sm3 class="pa-1"
              ><v-text-field
                v-model="amount"
                type="number"
                min="0.01"
                step="0.01"
                label="จำนวนเงิน"
              ></v-text-field
            ></v-flex>
            <v-flex xs12 sm4 class="pa-1"
              ><v-text-field
                v-model="note"
                label="เลขที่ออเดอร์ / หมายเหตุ (ไม่บังคับ)"
              ></v-text-field
            ></v-flex>
            <v-flex xs12 sm2 class="text-xs-right"
              ><v-btn
                color="primary"
                class="submit-modal-btn"
                :loading="saving"
                :disabled="!canSubmit"
                @click="submit"
                >ยืนยัน</v-btn
              ></v-flex
            >
          </v-layout>
        </v-card>
        <v-alert v-else-if="account" :value="true" type="info"
          >สิทธิ์ของคุณดูได้เฉพาะประวัติรายการ</v-alert
        >

        <v-data-table
          v-if="account"
          :headers="headers"
          :items="transactions"
          :pagination.sync="transactionPagination"
          class="transaction-table mt-4 elevation-1"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.create_at | dateTime }}</td>
            <td>{{ props.item.actor_user_id || "-" }}</td>
            <td>{{ selectedCompany.company_provider_wesmart_name }}</td>
            <td>{{ transactionTypeLabel(props.item.transaction_type) }}</td>
            <td class="text-xs-center">
              {{ money(props.item.balance_before) }}
            </td>
            <td
              class="text-xs-center"
              :style="{
                color:
                  props.item.balance_after - props.item.balance_before > 0
                    ? 'green'
                    : 'red',
              }"
            >
              <span
                v-if="props.item.balance_after - props.item.balance_before > 0"
                >+{{ money(props.item.amount) }}</span
              >
              <span
                v-else-if="
                  props.item.balance_after - props.item.balance_before < 0
                "
                >-{{ money(props.item.amount) }}</span
              >
              <span v-else>{{ money(props.item.amount) }}</span>
            </td>

            <td class="text-xs-center">
              {{ money(props.item.balance_after) }}
            </td>
            <td>{{ props.item.note || "-" }}</td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="snackbar" :color="snackbarColor">{{
      message
    }}</v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";
import { globalAxios } from "@/axios-path.js";

export default {
  filters: {
    dateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
  },
  data() {
    return {
      search: "",
      searchTimer: null,
      searching: false,
      saving: false,
      companies: [],
      selectedCompany: null,
      account: null,
      transactions: [],
      transactionPagination: {
        sortBy: "create_at",
        descending: true,
        rowsPerPage: 10,
      },
      permissions: {
        can_view: false,
        can_refill: false,
        can_withdraw: false,
      },
      action: null,
      amount: "",
      note: "",
      snackbar: false,
      snackbarColor: "success",
      message: "",
      headers: [
        { text: "วันที่", value: "create_at" },
        { text: "ผู้ทำรายการ (รหัสผู้ใช้)", value: "actor_user_id" },
        { text: "บริษัท", value: "company_id" },
        { text: "ประเภทรายการ", value: "transaction_type" },
        { text: "ยอดก่อนทำรายการ", value: "balance_before", align: "right" },
        { text: "จำนวนเงิน", value: "amount", align: "right" },
        { text: "ยอดหลังทำรายการ", value: "balance_after", align: "right" },
        { text: "หมายเหตุ", value: "note" },
      ],
    };
  },
  computed: {
    availableActions() {
      const actions = [];
      if (this.permissions.can_refill) {
        actions.push({ text: "เติมเครดิต", value: "refill" });
      }
      if (this.permissions.can_withdraw) {
        actions.push({ text: "ถอนเครดิต", value: "withdraw" });
      }
      return actions;
    },
    canMutate() {
      return this.availableActions.length > 0;
    },
    canSubmit() {
      return !!(this.selectedCompany && this.action && Number(this.amount) > 0);
    },
  },
  watch: {
    search(value) {
      clearTimeout(this.searchTimer);
      const query = String(value || "").trim();
      if (!query) {
        this.searchTimer = setTimeout(() => this.searchCompanies(""), 150);
        return;
      }
      if (query.length < 2) return;
      this.searchTimer = setTimeout(() => this.searchCompanies(query), 350);
    },
  },
  created() {
    this.searchCompanies("");
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    money(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async searchCompanies(query) {
      this.searching = true;
      try {
        const res = await globalAxios.get("/api/v1/mebf/contracts/companies", {
          params: { search: query },
        });
        const rows = Array.isArray(res) ? res : (res && res.data) || [];
        this.companies = rows.map((item) => {
          const rawTel = String(item.company_provider_wesmart_tel || "");
          const tel =
            rawTel && rawTel.charAt(0) !== "0" ? "0" + rawTel : rawTel;
          return Object.assign({}, item, {
            display_text:
              (item.company_provider_wesmart_name || "บริษัท") +
              " - " +
              (tel || item.company_provider_wesmart_email || ""),
          });
        });
      } catch (error) {
        this.notify("ไม่สามารถโหลดรายชื่อบริษัทได้", "error");
      } finally {
        this.searching = false;
      }
    },
    ensureCompanies() {
      if (!this.companies.length && !this.searching) this.searchCompanies("");
    },
    async loadCredit() {
      if (!this.selectedCompany) {
        this.account = null;
        this.transactions = [];
        return;
      }
      try {
        const companyId = this.selectedCompany.company_provider_wesmart_id;
        const res = await globalAxios.get(
          "/api/v1/mebf/contracts/company_credit",
          { params: { company_id: companyId } }
        );
        const payload = res && res.data && res.data.account ? res.data : res;
        this.account = payload.account;
        this.transactions = (payload.transactions || [])
          .slice()
          .sort((a, b) => {
            return (
              moment(b.create_at).valueOf() - moment(a.create_at).valueOf()
            );
          });
        this.permissions = Object.assign(
          {
            can_view: false,
            can_refill: false,
            can_withdraw: false,
          },
          payload.permissions || {}
        );
        const allowedValues = this.availableActions.map((item) => item.value);
        if (allowedValues.indexOf(this.action) === -1) {
          this.action = allowedValues.length ? allowedValues[0] : null;
        }
      } catch (error) {
        this.account = null;
        this.transactions = [];
        this.notify(
          this.errorMessage(error, "ไม่สามารถโหลดข้อมูลเครดิตบริษัทได้"),
          "error"
        );
      }
    },
    async submit() {
      if (!this.selectedCompany || !this.action || Number(this.amount) <= 0) {
        return this.notify("กรุณาระบุจำนวนเงินให้ถูกต้อง", "error");
      }
      this.saving = true;
      try {
        await globalAxios.post("/api/v1/mebf/contracts/company_credit", {
          company_id: this.selectedCompany.company_provider_wesmart_id,
          action: this.action,
          amount: this.amount,
          note: this.note,
          idempotency_key:
            "bo-" + Date.now() + "-" + Math.random().toString(36).slice(2),
        });
        this.amount = "";
        this.note = "";
        await this.loadCredit();
        this.notify("อัปเดตเครดิตบริษัทเรียบร้อยแล้ว", "success");
      } catch (error) {
        this.notify(
          this.errorMessage(error, "ไม่สามารถอัปเดตเครดิตบริษัทได้"),
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    notify(message, color) {
      this.message = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    transactionTypeLabel(type) {
      const labels = {
        top_up: "เติมเครดิต",
        withdraw: "ถอนเครดิต",
        order_deduction: "หักค่าออเดอร์",
        order_refund: "คืนเครดิตจากออเดอร์",
        opening_balance: "ยอดเครดิตเริ่มต้น",
        bonus_credit: "เครดิตโบนัส",
        manual_adjustment_credit: "ปรับเพิ่มเครดิต",
        manual_adjustment_debit: "ปรับลดเครดิต",
      };
      return labels[type] || type || "-";
    },
    errorMessage(error, fallback) {
      const detail =
        error && error.response && error.response.data
          ? error.response.data.detail
          : "";
      const messages = {
        "permission denied": "คุณไม่มีสิทธิ์ทำรายการนี้",
        "insufficient available balance": "ยอดเครดิตที่ใช้ได้ไม่เพียงพอ",
        "invalid amount": "จำนวนเงินไม่ถูกต้อง",
        "amount must be greater than zero": "จำนวนเงินต้องมากกว่า 0",
        "company not found": "ไม่พบบริษัท",
      };
      return messages[detail] || fallback;
    },
  },
};
</script>

<style scoped>
.company-credit-page {
  max-width: 1400px;
  min-height: 100vh;
  padding: 0 24px 40px;
}
.page-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 8px 24px;
}
.page-title {
  margin: 0 0 8px;
  color: #2c3e50;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  text-shadow: 2px 3px 12px rgba(33, 33, 32, 0.25);
}
.page-subtitle {
  margin: 0;
  color: #7f8c8d;
  font-size: 1.15rem;
}
.hero-icon {
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.58);
  box-shadow: 0 14px 35px rgba(31, 45, 61, 0.12);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.hero-icon .v-icon {
  color: #f3b400;
  font-size: 38px;
}
.glass-card,
.balance-card,
.credit-actions,
.transaction-table {
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76) !important;
  box-shadow: 0 16px 45px rgba(31, 45, 61, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.credit-shell {
  overflow: hidden;
}
.page-actions {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 14px;
}

.b2b-contract-btn {
  min-width: 104px;
  border-radius: 24px;
  padding-right: 24px;
  padding-left: 24px;
  box-shadow: 0 10px 24px rgba(33, 150, 243, 0.38) !important;
  transform: translateY(-2px);
}

.balance-card .caption {
  color: #7f8c8d;
}
.balance-card .headline {
  margin-top: 6px;
  color: #2c3e50;
  font-weight: 700;
}
.submit-modal-btn {
  min-width: 104px;
  border-radius: 24px;
  padding-right: 24px;
  padding-left: 24px;
  box-shadow: 0 8px 18px rgba(255, 193, 7, 0.34) !important;
  transform: translateY(-2px);
}
.submit-modal-btn.v-btn--disabled {
  background-color: #e0e0e0 !important;
  box-shadow: none !important;
  transform: none;
}
@media (max-width: 768px) {
  .company-credit-page {
    padding: 0 12px 28px;
  }
  .page-hero {
    text-align: center;
  }
  .page-title {
    font-size: 2rem;
  }
  .page-subtitle {
    font-size: 1rem;
  }
  .hero-icon {
    display: none;
  }
}
</style>
